import { Fragment } from "react";

// Convert a share URL into an embeddable player URL where possible.
function toEmbed(url) {
  try {
    const u = new URL(url);
    const h = u.hostname.replace("www.", "");
    if (h === "youtube.com" || h === "m.youtube.com") { const id = u.searchParams.get("v"); if (id) return `https://www.youtube.com/embed/${id}`; }
    if (h === "youtu.be") return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (h === "vimeo.com") return `https://player.vimeo.com/video/${u.pathname.split("/").filter(Boolean)[0]}`;
    if (h === "open.spotify.com") return `https://open.spotify.com/embed${u.pathname}`;
    if (h.endsWith("apple.com") && u.pathname.includes("/podcast")) return url.replace("podcasts.apple.com", "embed.podcasts.apple.com");
  } catch { /* ignore */ }
  return url;
}
const isVideoFile = (u) => /\.(mp4|webm|mov|ogg)(\?|$)/i.test(u);
const isAudioFile = (u) => /\.(mp3|wav|m4a|aac|ogg)(\?|$)/i.test(u);
const URL_RE = /(https?:\/\/[^\s]+)/g;

function AutoText({ text }) {
  const parts = (text || "").split(URL_RE);
  return parts.map((p, i) =>
    /^https?:\/\//.test(p)
      ? <a key={i} href={p} target="_blank" rel="noreferrer" className="text-secondary underline underline-offset-2 hover:text-secondary/80">{p}</a>
      : <Fragment key={i}>{p}</Fragment>
  );
}

function Embed({ token }) {
  const m = token.match(/^\[\[(\w+):([\s\S]+?)\]\]$/);
  if (!m) return null;
  const kind = m[1].toLowerCase();
  const [rawUrl, caption] = m[2].split("|");
  const url = (rawUrl || "").trim();
  if (!url) return null;

  if (kind === "image") return (
    <figure className="my-6" data-testid="embed-image">
      <img src={url} alt={caption || ""} className="w-full rounded-2xl object-cover" loading="lazy" />
      {caption && <figcaption className="mt-2 text-center text-sm text-muted-foreground">{caption}</figcaption>}
    </figure>
  );
  if (kind === "video") {
    if (isVideoFile(url)) return <video src={url} controls className="my-6 w-full rounded-2xl" data-testid="embed-video" />;
    return <div className="my-6 aspect-video overflow-hidden rounded-2xl" data-testid="embed-video"><iframe src={toEmbed(url)} title="video" className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>;
  }
  if (kind === "audio") return <audio src={url} controls className="my-6 w-full" data-testid="embed-audio" />;
  if (kind === "podcast" || kind === "embed") return (
    <div className="my-6 overflow-hidden rounded-2xl border border-border/60" data-testid="embed-frame">
      <iframe src={toEmbed(url)} title={caption || "embed"} className="h-[352px] w-full" loading="lazy" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture" allowFullScreen />
    </div>
  );
  if (kind === "link") return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="embed-link"
      className="my-4 flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-accent/40 px-4 py-3 transition-colors hover:border-secondary">
      <span className="min-w-0"><span className="block truncate font-medium text-foreground">{caption || url}</span><span className="block truncate text-xs text-muted-foreground">{url}</span></span>
      <span className="shrink-0 text-secondary">↗</span>
    </a>
  );
  return null;
}

// Renders article body: paragraphs, ## headings, - lists, auto-linked URLs, and [[embed]] tokens.
export default function RichBody({ body }) {
  const blocks = (body || "").split("\n\n");
  return (
    <div className="prose max-w-none" data-testid="rich-body">
      {blocks.map((block, i) => {
        const t = block.trim();
        if (/^\[\[(\w+):[\s\S]+\]\]$/.test(t)) return <Embed key={i} token={t} />;
        const lines = block.split("\n").filter(Boolean);
        if (lines.length && lines.every((l) => l.startsWith("- "))) {
          return <ul key={i} className="mb-5 list-disc space-y-1.5 pl-5 text-foreground/90">{lines.map((l, j) => <li key={j} className="leading-relaxed"><AutoText text={l.slice(2)} /></li>)}</ul>;
        }
        if (block.startsWith("## ")) {
          const [head, ...rest] = block.split("\n");
          const para = rest.join("\n").trim();
          return (
            <div key={i}>
              <h2 className="mb-3 mt-9 font-heading text-2xl font-light tracking-tight">{head.slice(3)}</h2>
              {para && <p className="mb-4 whitespace-pre-line leading-relaxed text-foreground/90"><AutoText text={para} /></p>}
            </div>
          );
        }
        if (!t) return null;
        return <p key={i} className="mb-4 whitespace-pre-line leading-relaxed text-foreground/90"><AutoText text={block} /></p>;
      })}
    </div>
  );
}
